import { useMemo, useState } from "react";

import { keyBy } from "lodash";

import { useUser } from "@components/user-context";
import { useUpdatePartnerConsent } from "@features/partner-consent/hooks/use-update-partner-consent";
import {
  FETCH_PARTNERS_CVE_CONSENT,
  UPSERT_PREFERRED_CVE_PARTNER,
} from "@gql/partner-consent";
import {
  useFeatureFlags,
  useNorthbeamQuery,
  useNorthbeamMutation,
} from "@hooks/http";
import {
  type Partner,
  PartnerPermissionEnum,
} from "@north-beam/nb-client-side";
import {
  type FetchPartnersCVEConsent_me_partnerCVEConsent,
  type FetchPartnersCVEConsent,
  type UpsertPreferredCvePartner,
  type UpsertPreferredCvePartnerVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import {
  DISPLAY_NAME_FOR_PARTNER_MAPPING,
  WHITELISTED_NB_USERS_FOR_PARTNER_CONSENT,
} from "@north-beam/nb-common/constants";

export const useCveConsentForPartners = () => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { user } = useUser();
  const [cveConsentForPartners, setCveConsentForPartners] = useState<
    FetchPartnersCVEConsent_me_partnerCVEConsent[]
  >([]);

  const [preferredCvePartner, setPreferredCvePartner] =
    useState<Partner | null>(null);

  const hasPartnerConsentFeatureFlag = hasFeatureFlag(
    "enablePartnersCVEConsentOnModelSettings",
  );

  const isUsingCrossPublisherCDV = hasFeatureFlag("useCVEArbiterTable");

  const { grantPartnerConsent, revokePartnerConsent } = useUpdatePartnerConsent(
    { permission: PartnerPermissionEnum.data_impression_ingestion },
  );

  const {
    data,
    loading: isLoadingCveConsent,
    refetch: refetchPartnersCveConsent,
  } = useNorthbeamQuery<FetchPartnersCVEConsent>(FETCH_PARTNERS_CVE_CONSENT, {
    doNotRaiseErrorOnFailure: true,
    skip: !hasPartnerConsentFeatureFlag,
    onCompleted: (partnersCveConsentResponse) => {
      const partnerCveConsent =
        partnersCveConsentResponse?.me?.partnerCVEConsent ?? [];
      setCveConsentForPartners(partnerCveConsent);

      const preferredPartner =
        partnersCveConsentResponse?.me?.preferredCvePartner;
      setPreferredCvePartner(preferredPartner);
    },
  });

  const canViewConsent = useMemo(() => {
    const hasProvidedConsentForAnyPartner =
      cveConsentForPartners.filter(
        (partnerConsent) => partnerConsent.hasConsent,
      ).length > 0;

    return hasPartnerConsentFeatureFlag || hasProvidedConsentForAnyPartner;
  }, [hasPartnerConsentFeatureFlag, cveConsentForPartners]);

  const canToggleConsent =
    user.isTrueAccountOwner ||
    WHITELISTED_NB_USERS_FOR_PARTNER_CONSENT.includes(user.authedUser.email);

  const canSubmitConsent =
    canToggleConsent &&
    (isUsingCrossPublisherCDV || preferredCvePartner !== null);

  const getPartnerDisplayName = (partner: Partner) =>
    DISPLAY_NAME_FOR_PARTNER_MAPPING[partner];

  const onChangeConsent = (partner: Partner, updatedHasConsent: boolean) => {
    setCveConsentForPartners((prev) =>
      prev.map((partnerConsent) => {
        if (partnerConsent.partner !== partner) {
          return partnerConsent;
        }
        return {
          ...partnerConsent,
          hasConsent: updatedHasConsent,
        };
      }),
    );

    if (isUsingCrossPublisherCDV) {
      return;
    }

    if (partner === preferredCvePartner && !updatedHasConsent) {
      setPreferredCvePartner(null);
    }

    if (!preferredCvePartner && updatedHasConsent) {
      setPreferredCvePartner(partner);
    }
  };

  const onChangePreferredCvePartner = (partner: Partner) => {
    setPreferredCvePartner(partner);
    onChangeConsent(partner, true);
  };

  const [upsertPreferredPartnerConsent] = useNorthbeamMutation<
    UpsertPreferredCvePartner,
    UpsertPreferredCvePartnerVariables
  >(UPSERT_PREFERRED_CVE_PARTNER);

  const onSubmitUpdatedConsent = async () => {
    const currentCveConsentForPartners = data?.me?.partnerCVEConsent ?? [];
    const currentCveConsentForPartnersMapping = keyBy(
      currentCveConsentForPartners,
      "partner",
    );

    const partnersWithConsentUpdated = cveConsentForPartners.filter(
      (updatedConsent) =>
        updatedConsent.hasConsent !==
        currentCveConsentForPartnersMapping[updatedConsent.partner]?.hasConsent,
    );
    if (partnersWithConsentUpdated.length === 0 && !preferredCvePartner) {
      return;
    }

    const partnersConsentUpdateMutations = partnersWithConsentUpdated.map(
      (partnerConsent) => {
        const consentUpdateMethod = partnerConsent.hasConsent
          ? grantPartnerConsent
          : revokePartnerConsent;

        return consentUpdateMethod(partnerConsent.partner);
      },
    );
    const preferredPartnerUpdate =
      !isUsingCrossPublisherCDV &&
      preferredCvePartner &&
      upsertPreferredPartnerConsent({
        variables: { partner: preferredCvePartner },
      });

    await Promise.all([
      ...partnersConsentUpdateMutations,
      preferredPartnerUpdate,
    ]);

    refetchPartnersCveConsent();
  };

  return {
    isLoadingCveConsent,
    cveConsentForPartners,
    onChangeConsent,
    preferredCvePartner,
    onChangePreferredCvePartner: onChangePreferredCvePartner,
    getPartnerDisplayName,
    canViewConsent,
    canToggleConsent,
    canSubmitConsent,
    onSubmitUpdatedConsent,
    isUsingCrossPublisherCDV,
  };
};
